import { useState } from 'react';
import { Loader2, Play, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtAmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const VERDICT_STYLES = {
  content_match_viable: {
    border: 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950',
    icon: CheckCircle2,
    iconClass: 'text-green-600 dark:text-green-400',
  },
  content_ambiguous: {
    border: 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950',
    icon: AlertTriangle,
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  no_content_match: {
    border: 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950',
    icon: XCircle,
    iconClass: 'text-red-600 dark:text-red-400',
  },
};

const MATCH_CLASS_COLORS = {
  unique: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  ambiguous: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  absent: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

export function InvoiceContentCorrelationPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.invoiceContentCorrelation();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const vs = result ? VERDICT_STYLES[result.verdict] || VERDICT_STYLES.no_content_match : null;
  const VerdictIcon = vs?.icon;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Invoice Content Correlation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Tests whether bros invoices can be matched to Xero invoices by CONTENT
          (customer + amount + currency + date window), ignoring the invoice
          number entirely. DB-only, zero Xero calls.
        </p>
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run Diagnostic
        </Button>

        {result && (
          <div className="space-y-4">
            {/* Verdict */}
            <div className={`flex items-start gap-2 p-3 rounded-lg border ${vs.border}`}>
              <VerdictIcon className={`h-5 w-5 mt-0.5 shrink-0 ${vs.iconClass}`} />
              <div>
                <p className="text-sm font-medium" data-testid="content-verdict">{result.verdict}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{result.verdict_reason}</p>
              </div>
            </div>

            {/* Summary counts */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-[10px] text-muted-foreground">Sample</p>
                <p className="text-lg font-semibold tabular-nums">{result.sample_size}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-[10px] text-muted-foreground">Unique</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{result.summary?.unique_content_match}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-[10px] text-muted-foreground">Ambiguous</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{result.summary?.ambiguous_content_match}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-[10px] text-muted-foreground">No match</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{result.summary?.no_content_match}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-[10px] text-muted-foreground">Number differs</p>
                <p className="text-lg font-semibold tabular-nums text-blue-600">{result.summary?.number_differs}</p>
              </div>
            </div>

            {/* Per-invoice comparisons */}
            {result.comparisons && result.comparisons.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Per-invoice comparisons ({result.comparisons.length})
                </summary>
                <div className="mt-2 space-y-3 max-h-[500px] overflow-y-auto">
                  {result.comparisons.map((c, i) => (
                    <div key={c.bros_invoice_number || i} className="border rounded-lg p-3 space-y-2">
                      {/* Bros invoice header */}
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="text-[10px] font-mono">{c.bros_invoice_number}</Badge>
                        <Badge className={`text-[10px] ${MATCH_CLASS_COLORS[c.match_class] || ''}`}>
                          {c.match_class}
                        </Badge>
                        {c.number_differs && (
                          <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                            number differs
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground grid grid-cols-2 sm:grid-cols-4 gap-1">
                        <span>Customer: <span className="text-foreground">{c.bros_customer || '-'}</span></span>
                        <span>Amount: <span className="font-mono text-foreground">{fmtAmt(c.bros_amount)}</span></span>
                        <span>Currency: <span className="text-foreground">{c.bros_currency || '-'}</span></span>
                        <span>Date: <span className="text-foreground">{c.bros_issue_date || '-'}</span></span>
                      </div>

                      {/* Content candidates */}
                      {c.content_candidates && c.content_candidates.length > 0 ? (
                        <div className="pl-3 border-l-2 border-green-300 dark:border-green-700 space-y-1">
                          <p className="text-[10px] font-medium text-muted-foreground">
                            Xero content match{c.content_candidates.length > 1 ? 'es' : ''} ({c.candidates_3d_count} within 3d, {c.candidates_0d_count} exact date)
                          </p>
                          {c.content_candidates.map((x, j) => (
                            <div key={j} className="text-[10px] grid grid-cols-2 sm:grid-cols-5 gap-1 bg-muted/30 rounded p-1.5">
                              <span className="font-mono">{x.xero_invoice_number}</span>
                              <span className="font-mono">{fmtAmt(x.xero_amount)}</span>
                              <span>{x.xero_currency}</span>
                              <span>{x.xero_issue_date || '-'}</span>
                              <span className="truncate text-muted-foreground">{x.xero_customer}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[10px] text-muted-foreground pl-3 border-l-2 border-red-300 dark:border-red-700">
                          No content match in Xero (customer + amount + currency + date window)
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Generated: {result.generated_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
